import { useMutation, useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";
import { showErrorAlert } from "../../utils/showErrorAlert";
import { showSuccessToast } from "../../utils/showToastAlert";

/**
 * @param {object} deps
 * @param {(url: string, options: any) => Promise<any>} deps.callApiWithWorker
 * @param {(key: string, opts?: any) => string} deps.t
 */
export function useDeletePolicy({ callApiWithWorker, t }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["deletePolicy"],

    mutationFn: async ({ id }) => {
      if (!id) throw new Error("Missing policy id");

      try {
        const res = await callApiWithWorker(
          `/policy-templates/${encodeURIComponent(id)}`,
          { method: "DELETE" },
        );

        const statusCode =
          res?.statusCode ??
          res?.data?.statusCode ??
          res?.status ??
          res?.data?.status;
        if (statusCode && statusCode >= 400) {
          const error = new Error(`Delete failed (${statusCode})`);
          error.statusCode = statusCode;
          throw error;
        }

        return res;
      } catch (e) {
        const msg = String(e?.message || e);
        if (msg.includes("Unexpected end of JSON input")) {
          return { statusCode: 204, body: "" };
        }

        const statusCode = e?.statusCode ?? e?.response?.status;
        const error = new Error(
          e?.message || `Delete failed (${statusCode ?? "unknown"})`,
        );
        error.statusCode = statusCode;
        throw error;
      }
    },

    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["policies"] });

      if (variables?.id) {
        await queryClient.invalidateQueries({
          queryKey: ["policy", variables.id],
        });
      }

      showSuccessToast(t?.("common.deleteSuccessMessage") || "Deleted successfully", {
        title: t?.("common.deleteSuccessTitle") || "Deleted!",
      });
    },

    onError: async (error) => {
      if (error?.statusCode === 409) {
        await Swal.fire({
          icon: "warning",
          title: t?.("common.cannotDelete") || "No se pudo borrar",
          text:
            t?.("common.deleteConflict") ||
            "Este elemento está actualmente en uso, por favor, elimine los elementos asociados",
        });
        return;
      }

      showErrorAlert(error, t);
    },
  });
}
